import React, { useEffect, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { usePurchasing } from '@features/purchasing/hooks/usePurchasing';
import { CreatePurchaseOrderFlow } from '@features/purchasing/components/create/CreatePurchaseOrderFlow';
import { PurchaseOrderDraft } from '@features/purchasing/components/details/PurchaseOrderDraft';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { useTutorialStore } from '../../../../application/store/TutorialStore';
import { PurchaseOrder } from '@domain/purchasing';
import OnboardingOverlayCard from '../../shared/OnboardingOverlayCard';
import OnboardingEntityEmptyRow from '../../shared/OnboardingEntityEmptyRow';
import OnboardingEntityList from '../../shared/OnboardingEntityList';
import OnboardingSkipFooter from '../../shared/OnboardingSkipFooter';
import PurchaseOrderIntroStep from './PurchaseOrderIntroStep';

interface PurchaseOrderOnboardingStepProps {
  onDismiss: () => void;
}

const PurchaseOrderOnboardingStep: React.FC<PurchaseOrderOnboardingStepProps> = ({
  onDismiss,
}) => {
  const { purchaseOrders } = usePurchasing();
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();
  const { markStepCompleted, setCurrentStep } = useTutorialStore();
  const [showFinished, setShowFinished] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);

  const orders = purchaseOrders ?? [];
  const hasOrders = orders.length > 0;

  useEffect(() => {
    if (!hasOrders) return;

    markStepCompleted('purchase-order');
    setCurrentStep(5);
    setShowFinished(true);
  }, [hasOrders, markStepCompleted, setCurrentStep]);

  const handleCreatePurchaseOrder = () => {
    openBottomSheet({
      title: 'Purchase Order Draft',
      component: (
        <CreatePurchaseOrderFlow
          handleClose={async () => {
            await closeBottomSheet();
          }}
        />
      ),
    });
  };

  const handleOpenOrder = (order: PurchaseOrder) => {
    if (order.draft && order.id) {
      openBottomSheet({
        title: 'Edit Purchase Order Draft',
        component: <PurchaseOrderDraft purchaseOrderId={order.id} />,
        closeOnBackdropClick: false,
      });
      return;
    }
    onDismiss();
  };

  if (showFinished || hasOrders) {
    return (
      <OnboardingOverlayCard
        title="Your first purchase order is ready"
        footer={
          <Button variant="contained" onClick={onDismiss}>
            View purchase orders
          </Button>
        }
      >
        <Typography variant="body2">
          You can manage purchase orders and shipments from the purchasing page.
        </Typography>

        <OnboardingEntityList
          items={orders
            .filter((order): order is PurchaseOrder & { id: string } => Boolean(order.id))
            .map((order) => ({
              id: order.id,
              primary: order.poNumber || 'Purchase order',
              secondary: order.supplier?.name || 'Purchase order',
              avatarFallback: (order.poNumber || 'P').charAt(0).toUpperCase(),
              chipLabel: order.draft ? 'Draft' : 'Placed',
              chipColor: order.draft ? ('default' as const) : ('success' as const),
              onClick: () => handleOpenOrder(order),
            }))}
        />
      </OnboardingOverlayCard>
    );
  }

  if (!introComplete) {
    return <PurchaseOrderIntroStep onComplete={() => setIntroComplete(true)} />;
  }

  return (
    <OnboardingOverlayCard
      title="Create your first purchase order"
      footer={<OnboardingSkipFooter onDismiss={onDismiss} />}
    >
      <Typography variant="body2">
        Place an order with your suppliers so stock arrives before you run out.
      </Typography>

      <OnboardingEntityEmptyRow
        title="Create a purchase order"
        description="Start a draft order with one of your suppliers"
        actionLabel="Create order"
        onAction={handleCreatePurchaseOrder}
      />
    </OnboardingOverlayCard>
  );
};

export default PurchaseOrderOnboardingStep;
